import type { PolygonPoint as Point } from "./C1_polygonPlacement";

const EPS = 1e-7;
const cross = (a: Point, b: Point) => a.x * b.y - a.y * b.x;
const minus = (a: Point, b: Point): Point => ({ x: a.x - b.x, y: a.y - b.y });
const area = (p: readonly Point[]) => p.reduce((sum, a, i) => sum + cross(a, p[(i + 1) % p.length]), 0) / 2;
const key = (p: Point) => `${Math.round(p.x / EPS)},${Math.round(p.y / EPS)}`;
const clean = (p: readonly Point[]) => p.filter((a, i) => key(a) !== key(p[(i + 1) % p.length]));
const simplify = (p: readonly Point[]) => {
  let result = clean(p);
  while (result.length >= 3) {
    const index = result.findIndex((a, i) => Math.abs(cross(
      minus(a, result[(i + result.length - 1) % result.length]),
      minus(result[(i + 1) % result.length], a),
    )) < EPS);
    if (index < 0) break;
    result.splice(index, 1);
    result = clean(result);
  }
  return result;
};

/** Trace simple faces, splitting crossings and overlapping edges before following the boundary. */
function faces(points: readonly Point[]): Point[][] {
  const graph = new Map<string, { point: Point; neighbors: Point[] }>();
  const connect = (a: Point, b: Point) => {
    if (key(a) === key(b)) return;
    for (const [start, end] of [[a, b], [b, a]]) {
      let node = graph.get(key(start));
      if (!node) graph.set(key(start), node = { point: start, neighbors: [] });
      if (!node.neighbors.some(p => key(p) === key(end))) node.neighbors.push(end);
    }
  };
  points.forEach((a, i) => {
    const b = points[(i + 1) % points.length], direction = minus(b, a);
    const lengthSquared = direction.x ** 2 + direction.y ** 2;
    if (lengthSquared < EPS ** 2) return;
    const splits = [0, 1];
    points.forEach((c, j) => {
      const d = points[(j + 1) % points.length], other = minus(d, c), delta = minus(c, a);
      const denominator = cross(direction, other);
      if (Math.abs(denominator) > EPS) {
        const t = cross(delta, other) / denominator, u = cross(delta, direction) / denominator;
        if (t > EPS && t < 1 - EPS && u >= -EPS && u <= 1 + EPS) splits.push(t);
      } else if (Math.abs(cross(delta, direction)) < EPS) {
        for (const point of [c, d]) {
          const t = ((point.x - a.x) * direction.x + (point.y - a.y) * direction.y) / lengthSquared;
          if (t > EPS && t < 1 - EPS) splits.push(t);
        }
      }
    });
    splits.sort((x, y) => x - y);
    const at = (t: number) => ({ x: a.x + direction.x * t, y: a.y + direction.y * t });
    for (let k = 1; k < splits.length; k++) connect(at(splits[k - 1]), at(splits[k]));
  });
  for (const node of graph.values()) node.neighbors.sort((a, b) =>
    Math.atan2(a.y - node.point.y, a.x - node.point.x) - Math.atan2(b.y - node.point.y, b.x - node.point.x));
  const visited = new Set<string>(), result: Point[][] = [];
  for (const node of graph.values()) for (const neighbor of node.neighbors) {
    let a = node.point, b = neighbor;
    const polygon: Point[] = [];
    while (!visited.has(`${key(a)}>${key(b)}`)) {
      visited.add(`${key(a)}>${key(b)}`);
      polygon.push(a);
      const next = graph.get(key(b))!;
      const index = next.neighbors.findIndex(p => key(p) === key(a));
      a = b;
      b = next.neighbors[(index + next.neighbors.length - 1) % next.neighbors.length];
    }
    if (area(polygon) > EPS) result.push(simplify(polygon));
  }
  return result;
}

/**
 * Keep the largest simple room footprint. For orthogonal rooms, also remove strips
 * narrower than the minimum wall-axis spacing. Coordinates are never rounded to a grid.
 * An empty result denotes a fully collapsed room; undefined denotes unsupported input.
 */
export function validateRoomPolygon(input: readonly Point[], minimumWidth: number): Point[] | undefined {
  if (input.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y)) || input.length > 256) return;
  const points = clean(input);
  if (points.length < 3) return [];
  const loops = faces(points).sort((a, b) => area(b) - area(a));
  let polygon = loops[0];
  if (!polygon) return [];
  // A hole joined by a retraced bridge cannot be represented by this room model.
  if (new Set(polygon.map(key)).size !== polygon.length) return;
  const orthogonal = polygon.every((p, i) => {
    const next = polygon[(i + 1) % polygon.length];
    return Math.abs(p.x - next.x) < EPS || Math.abs(p.y - next.y) < EPS;
  });
  if (orthogonal && minimumWidth > EPS) {
    // The face tracer can produce different floating point representations of
    // the same axis. Separate cells for those values create microscopic strips
    // whose removal can disconnect the boundary. Coalesce within the geometry
    // tolerance, retaining actual coordinates rather than rounding to a grid.
    const coords = (axis: "x" | "y") => {
      const result: number[] = [];
      for (const value of polygon.map(p => p[axis]).sort((a, b) => a - b)) {
        if (!result.length || value - result[result.length - 1] > EPS) result.push(value);
      }
      return result;
    };
    const xs = coords("x"), ys = coords("y");
    if (xs.length * ys.length > 10000) return;
    const width = xs.length - 1, height = ys.length - 1;
    const inside = (x: number, y: number) => {
      let contained = false;
      polygon.forEach((a, i) => {
        const b = polygon[(i + 1) % polygon.length];
        if ((a.y > y) !== (b.y > y) && x < (b.x - a.x) * (y - a.y) / (b.y - a.y) + a.x) contained = !contained;
      });
      return contained;
    };
    const cells = Array.from({ length: height }, (_, y) => Array.from({ length: width }, (_, x) =>
      inside((xs[x] + xs[x + 1]) / 2, (ys[y] + ys[y + 1]) / 2)));
    let changed = true;
    while (changed) {
      const remove: [number, number][] = [];
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (!cells[y][x]) continue;
        let left = x, right = x, top = y, bottom = y;
        while (left > 0 && cells[y][left - 1]) left--;
        while (right + 1 < width && cells[y][right + 1]) right++;
        while (top > 0 && cells[top - 1][x]) top--;
        while (bottom + 1 < height && cells[bottom + 1][x]) bottom++;
        if (xs[right + 1] - xs[left] < minimumWidth - EPS || ys[bottom + 1] - ys[top] < minimumWidth - EPS) remove.push([x, y]);
      }
      changed = remove.length > 0;
      for (const [x, y] of remove) cells[y][x] = false;
    }
    // Cancel interior edges, then trace the remaining contours (including point contacts).
    const edges = new Map<string, [Point, Point]>();
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
      if (!cells[y][x]) continue;
      const corners = [{ x: xs[x], y: ys[y] }, { x: xs[x + 1], y: ys[y] },
        { x: xs[x + 1], y: ys[y + 1] }, { x: xs[x], y: ys[y + 1] }];
      corners.forEach((a, i) => {
        const b = corners[(i + 1) % 4], reverse = `${key(b)}>${key(a)}`;
        if (edges.has(reverse)) edges.delete(reverse); else edges.set(`${key(a)}>${key(b)}`, [a, b]);
      });
    }
    // Feed each boundary component to the face tracer without adding connecting edges.
    const rings: Point[][] = [];
    while (edges.size) {
      const first = edges.values().next().value!;
      const ring: Point[] = [first[0]];
      let edge: [Point, Point] | undefined = first;
      while (edge) {
        edges.delete(`${key(edge[0])}>${key(edge[1])}`);
        const end: Point = edge[1];
        if (key(end) === key(ring[0])) break;
        ring.push(end);
        edge = [...edges.values()].find(candidate => key(candidate[0]) === key(end));
      }
      if (area(ring) > EPS) rings.push(...faces(ring));
    }
    polygon = rings.sort((a, b) => area(b) - area(a))[0];
    if (!polygon) return [];
  }
  // Retain the original winding so side names and wall attachments remain consistent.
  if (area(points) < 0) polygon.reverse();
  return polygon.map(p => ({ ...p }));
}
